import { MONTH_COLUMN_KEY, YEAR_COLUMN_KEY } from 'business/constants';
import { useQuery } from '@tanstack/react-query';
import {
    Response,
    Absence,
    Echelon,
    PayComponentSalaryMassLine,
    SalaryMassLine,
    SupCompHoursLine,
    VacantPosition,
} from 'routes/HumanResourcesModule/business/types';
import { MonthNumber } from 'routes/HumanResourcesModule/PayState/helpers/types';
import { RQAPICallWithData } from 'store/APIClient';
import { pivotedKeysPostProcessingMiddleware } from '../queryHelpers';
import { PayStateIndicatorItem } from '../../PayStateIndicators/helpers/types';

export const payStateDataQueryKey = (payStateId: number) =>
    `pay_state_data_${payStateId}`;

/**
 * Get the salary mass (SM) data for permanent or non-permanent agent.
 *
 * @param payStateId - Id of the paystate
 * @param isMonthly - True for monthly SM data, otherwise it will be yearly.
 * @param permanent - True to get the SM data for permanent agent only, and false to get only non-permanent agent.
 */
export const useGetSalaryMassData = (
    payStateId: number,
    isMonthly: boolean,
    permanent: boolean,
) =>
    useQuery({
        queryKey: [
            payStateDataQueryKey(payStateId),
            'salary_mass',
            isMonthly,
            permanent,
        ],

        queryFn: async () => {
            const response = await RQAPICallWithData<Response<SalaryMassLine>>({
                endpoint: `human_resources/pay_state/${payStateId}/salary_mass_data/${
                    isMonthly ? MONTH_COLUMN_KEY : YEAR_COLUMN_KEY
                }?permanent=${permanent ? 1 : 0}`,
            });
            return response.content.data;
        },
    });

export const useGetPayComponentSalaryMassData = (
    payStateId: number,
    month: MonthNumber,
    permanent: boolean,
) =>
    useQuery({
        queryKey: ['pay_component_salary_mass', payStateId, month, permanent],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                Response<PayComponentSalaryMassLine>
            >({
                // In this case, we get a bunch of keys pivoted by Pay Component values, and those
                // values will then be displayed as columns headers, so we want them to be in a human readable format
                // and so, we need not to camelize them as usual keys. So we skip the classical case conversion
                // and use a custom postProcessingCallback to do as we want.
                endpoint: `human_resources/pay_state/${payStateId}/pay_component_salary_mass/month/${month}?permanent=${permanent ? 1 : 0}`,
                skipCaseConversion: true,
                postProcessingCallback: pivotedKeysPostProcessingMiddleware,
            });
            return response.content.data;
        },
    });

export const useGetSupCompHoursData = (
    payStateId: number,
    month: MonthNumber,
) =>
    useQuery({
        queryKey: [payStateDataQueryKey(payStateId), 'sup_comp_hours', month],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                Response<SupCompHoursLine>
            >({
                endpoint: `human_resources/pay_state/${payStateId}/sup_comp_hours_data/${month}`,
            });
            return response.content.data;
        },
    });

export const useGetAbsenceCostsEndpoint = (
    payStateId: number,
    isMonthly: boolean,
) =>
    useQuery({
        queryKey: [
            payStateDataQueryKey(payStateId),
            'absence_costs',
            isMonthly,
        ],

        queryFn: async () => {
            const response = await RQAPICallWithData<Response<Absence>>({
                endpoint: `human_resources/pay_state/${payStateId}/absence_costs_data/${
                    isMonthly ? MONTH_COLUMN_KEY : YEAR_COLUMN_KEY
                }`,
            });
            return response.content.data;
        },
    });

export const useGetVacantPositionsData = (payStateId: number) =>
    useQuery({
        queryKey: [payStateDataQueryKey(payStateId), 'vacant_positions'],

        queryFn: async () => {
            const response = await RQAPICallWithData<Response<VacantPosition>>({
                endpoint: `human_resources/pay_state/${payStateId}/vacant_positions_data`,
            });
            return response.content.data;
        },
    });

export const useGetEchelonsData = (payStateId: number) =>
    useQuery({
        queryKey: [payStateDataQueryKey(payStateId), 'echelons'],

        queryFn: async () => {
            const response = await RQAPICallWithData<Response<Echelon>>({
                endpoint: `human_resources/pay_state/${payStateId}/echelons_data`,
            });
            return response.content.data;
        },
    });

export const useGetIndicatorSalaryMassData = (payStateId: number) =>
    useQuery({
        queryKey: [payStateDataQueryKey(payStateId), `indicator_salary_mass`],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                Response<PayStateIndicatorItem>
            >({
                endpoint: `human_resources/pay_state/${payStateId}/global_salary_mass_indicator`,
            });
            return response.content.data;
        },
    });

export const useGetIndicatorSupCompData = (payStateId: number) =>
    useQuery({
        queryKey: [payStateDataQueryKey(payStateId), 'indicator_sup_comp'],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                Response<PayStateIndicatorItem>
            >({
                endpoint: `human_resources/pay_state/${payStateId}/global_sup_comp_indicator`,
            });
            return response.content.data;
        },
    });
